import { useId, type ReactNode } from 'react'
import { Checkbox } from '@/components/ui/Checkbox'
import { Switch } from '@/components/ui/Switch'
import { cn } from '@/utils/cn'

interface ToggleFieldProps {
  label: ReactNode
  description?: ReactNode
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  kind?: 'checkbox' | 'switch'
  disabled?: boolean
  className?: string
}

/** A checkbox or switch with an inline label and optional help text. */
export function ToggleField({
  label,
  description,
  checked,
  onCheckedChange,
  kind = 'checkbox',
  disabled,
  className,
}: ToggleFieldProps) {
  const id = useId()
  const descId = description ? `${id}-desc` : undefined
  const control =
    kind === 'switch' ? (
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-describedby={descId}
      />
    ) : (
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(c) => onCheckedChange(c === true)}
        disabled={disabled}
        aria-describedby={descId}
        className="mt-0.5"
      />
    )

  return (
    <div
      className={cn(
        'flex items-start gap-3',
        kind === 'switch' && 'flex-row-reverse justify-between',
        className,
      )}
    >
      {control}
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="text-sm font-medium text-ink">
          {label}
        </label>
        {description ? (
          <p id={descId} className="mt-0.5 text-xs text-muted">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  )
}
